/**
 * What each storage type is, one entry per type, in one reviewable table.
 *
 * ## Why this is a separate file and not a field on `GGML_TYPES`
 *
 * Two reasons, both the reviewer's (2026-08-19).
 *
 * `GGML_TYPES` describes 35 ggml block formats. The types a visitor actually
 * hovers are the **union** of those and the safetensors header dtypes — `U8`,
 * `F8_E4M3`, `BOOL` and the rest — which have no ggml id and would have nowhere
 * to live on that map. One table covers both families; a field on one of them
 * covers half the surface.
 *
 * And the educational layer's premise is that copy is reviewed **as a set, in
 * one voice** (`explain.ts` opens by saying so). Forty-two sentences distributed
 * across a type table are forty-two sentences nobody reads together, which is
 * the failure mode that file exists to prevent. So this is content in one place,
 * and `explain.ts`'s plumbing renders it.
 *
 * ## The register
 *
 * `explain.ts`'s, unchanged: what it is, then the tradeoff. Concrete, exact
 * about limits, no adjective doing a fact's job. In particular **no bare
 * "better" or "worse"** — a type is smaller or larger, holds more or fewer
 * distinct levels, spends its bits on scales or on weights. Which of those is
 * better depends on the model, and the reader is the one who knows their model.
 *
 * Every bits-per-weight figure here is `typeSize × 8 ÷ blockSize` computed from
 * `GGML_TYPES` — the same arithmetic the app does — and `dtype-explanations.test.ts`
 * asserts that any figure quoted in the copy matches the table. A sentence
 * cannot drift from the geometry it describes without failing a test.
 *
 * ## Per-tensor type, never the file-level recipe
 *
 * A cell holds a *per-tensor* type: `Q4_K`. `Q4_K_M` and `Q4_K_S` are names for
 * a **file-level mix** — a recipe that stores some tensors as `Q4_K`, others as
 * `Q6_K` or `Q5_K`, chosen per tensor role. Explaining `Q4_K` as though it were
 * `Q4_K_M` would tell a reader their whole file is one type when the map beside
 * the tooltip is showing them it is not.
 *
 * So no entry here mentions `_M`/`_S`. The recipe idea is explained once, in
 * `explain.ts`'s generic `dtype` entry, where it belongs.
 *
 * ## When a type has no entry
 *
 * `explainForDtype` returns `undefined` and the surface omits the sentence.
 * There is no fallback string and no generated copy — the same posture as
 * `ggmlTypeName`, which reports an unrecognized type by its id rather than
 * guessing a name. A type we cannot describe gets silence, which is honest,
 * rather than a paragraph that sounds authoritative about a format nobody here
 * has read.
 */

import type { Explanation } from "./explain.js";

/**
 * Types that are deliberately left without copy.
 *
 * Empty today, and it exists so that the coverage test has somewhere to record
 * a decision rather than being weakened when one is made. Adding a name here is
 * a choice with a reason attached; leaving a type out of both this list and the
 * table below fails the test.
 */
export const NO_COPY: readonly string[] = [];

/**
 * Every storage type a tensor can be stored in, explained.
 *
 * Ordered by family rather than by id, because that is the order they make
 * sense in and this table is read top to bottom by whoever reviews it: plain
 * floats, then integers, then the legacy ggml blocks, then K-quants, then
 * i-quants, then ternary, then the 4-bit float formats.
 */
export const DTYPE_EXPLANATIONS: Readonly<Record<string, Explanation>> = {
  /* -- plain floating point ------------------------------------------------ */

  F32: {
    term: "F32",
    body: `A 32-bit IEEE float: one sign bit, 8 exponent bits, 23 significant bits.
      Four bytes per weight, and the format almost every model is trained in. Nothing
      is approximated, so a file stored this way is as large as the model gets.`,
  },

  F16: {
    term: "F16",
    body: `A 16-bit IEEE float: 5 exponent bits and 10 significant bits. Half the size
      of <code>F32</code> with about three decimal digits of precision, but the exponent
      only reaches about <code>±65504</code> — values beyond that become infinity, which
      is why training usually prefers <code>BF16</code>.`,
  },

  BF16: {
    term: "BF16",
    body: `A 16-bit float that keeps <code>F32</code>'s 8 exponent bits and cuts the
      significant bits to 7. Same range as <code>F32</code>, roughly two decimal digits
      of precision. Trading precision for range is what makes it the common training
      format: an overflow is fatal, a rounding error is absorbed.`,
  },

  F64: {
    term: "F64",
    body: `A 64-bit IEEE double — eight bytes per element. Almost never a weight; where
      it appears it is usually a constant or a statistic carried over from code that
      computed it in double.`,
  },

  F8_E4M3: {
    term: "F8_E4M3",
    body: `An 8-bit float with 4 exponent bits and 3 significant bits, reaching about
      <code>±448</code>. One byte per weight with a real exponent, so it holds a wide
      spread of magnitudes at low precision — the pairing is usually a per-tensor or
      per-block scale stored separately.`,
  },

  F8_E5M2: {
    term: "F8_E5M2",
    body: `An 8-bit float with 5 exponent bits and 2 significant bits, reaching about
      <code>±57344</code>. The same byte as <code>F8_E4M3</code> spent differently:
      more range, fewer significant digits. Gradients use it more often than weights,
      because range matters more there than precision.`,
  },

  /* -- integers and flags -------------------------------------------------- */

  I8: {
    term: "I8",
    body: `A signed 8-bit integer, <code>-128</code> to <code>127</code>. As a weight
      it is quantized data that needs a scale to mean anything; as anything else it is
      usually an index or a small constant.`,
  },

  I16: {
    term: "I16",
    body: `A signed 16-bit integer. In weight files it is most often a packed container
      rather than a number to read directly — EXL3 stores its trellis payload this way,
      where the 16 bits hold several weights rather than one.`,
  },

  I32: {
    term: "I32",
    body: `A signed 32-bit integer. In a quantized repo this is usually a
      <strong>packed</strong> tensor: AWQ and GPTQ store four or eight weights inside
      each 32-bit word, so its element count is a word count and not a parameter count.`,
  },

  I64: {
    term: "I64",
    body: `A signed 64-bit integer — eight bytes each. Not a weight: position ids,
      token ids and shape metadata are what usually carry this type.`,
  },

  U8: {
    term: "U8",
    body: `An unsigned byte, <code>0</code> to <code>255</code>. In a quantized
      safetensors repo this almost always means <strong>packed</strong>: two 4-bit
      weights per byte, or a block payload rebuilt with separate scale and bias planes.
      <code>U8</code> is the container, not the weight width.`,
  },

  U16: {
    term: "U16",
    body: `An unsigned 16-bit integer. Rare in weight files; where it appears it is
      normally an index or a packed container rather than a value to read directly.`,
  },

  U32: {
    term: "U32",
    body: `An unsigned 32-bit integer, and in quantized repos usually a packed payload
      — GPTQ's <code>qweight</code> and the packed-integer schemes store several
      weights per word. Counting these as parameters overcounts by the packing factor.`,
  },

  U64: {
    term: "U64",
    body: `An unsigned 64-bit integer. Not a weight type; it carries counts, offsets or
      identifiers.`,
  },

  BOOL: {
    term: "BOOL",
    body: `One byte per element holding a true/false flag. Attention masks are the
      usual case — real bytes in the file that are not weights, which is why they are
      counted in the file's size and left out of its parameter count.`,
  },

  /* -- legacy ggml blocks (32 weights per block) --------------------------- */

  Q4_0: {
    term: "Q4_0",
    body: `32 weights in 18 bytes — <strong>4.50 bits each</strong>. One 16-bit scale
      per block, then 4 bits per weight holding a signed level. Symmetric: there is no
      offset, so the block's values are assumed centred on zero.`,
  },

  Q4_1: {
    term: "Q4_1",
    body: `32 weights in 20 bytes — <strong>5.00 bits each</strong>. <code>Q4_0</code>
      plus a second 16-bit value giving the block's minimum, so the 16 levels can sit
      anywhere rather than being centred on zero. The extra half-bit per weight buys
      that offset.`,
  },

  Q5_0: {
    term: "Q5_0",
    body: `32 weights in 22 bytes — <strong>5.50 bits each</strong>. 32 levels per
      weight with one scale per block, stored as 4 bits in a nibble plus a fifth bit
      collected into a separate 32-bit field.`,
  },

  Q5_1: {
    term: "Q5_1",
    body: `32 weights in 24 bytes — <strong>6.00 bits each</strong>. <code>Q5_0</code>
      with a block minimum as well as a scale, the same offset <code>Q4_1</code> adds
      to <code>Q4_0</code>.`,
  },

  Q8_0: {
    term: "Q8_0",
    body: `32 weights in 34 bytes — <strong>8.50 bits each</strong>. One 16-bit scale
      and one signed byte per weight. 256 levels per block is close enough to the
      original that it is normally used where a tensor is too sensitive to quantize
      harder.`,
  },

  Q8_1: {
    term: "Q8_1",
    body: `32 weights in 36 bytes — <strong>9.00 bits each</strong>. <code>Q8_0</code>
      with a second 16-bit field per block holding the sum of the block's values, which
      some dot-product paths use to avoid recomputing it.`,
  },

  /* -- K-quants (256-weight superblocks, scales quantized too) ------------- */

  Q2_K: {
    term: "Q2_K",
    body: `256 weights in 84 bytes — <strong>2.63 bits each</strong>. The superblock
      splits into 16 groups of 16, each with its own 4-bit scale and minimum, and those
      per-group values get their own block-level scale. Two bits per weight leaves four
      levels; the scales and minimums that place them take about a quarter of the
      block's bytes.`,
  },

  Q3_K: {
    term: "Q3_K",
    body: `256 weights in 110 bytes — <strong>3.44 bits each</strong>. Three bits per
      weight, eight levels, with 6-bit scales for 16 sub-blocks. The K-family's habit
      is visible here: quantize the scales as well as the weights, and spend the saving
      on more sub-blocks.`,
  },

  Q4_K: {
    term: "Q4_K",
    body: `256 weights in 144 bytes — <strong>4.50 bits each</strong>. Eight groups of
      32, each with a 6-bit scale and a 6-bit minimum, and 4 bits per weight. It costs
      the same as <code>Q4_0</code> and spends it differently: both carry one scale per
      32 weights, but <code>Q4_0</code> assumes the block centres on zero, where this
      stores a minimum per group so the levels can sit anywhere.`,
  },

  Q5_K: {
    term: "Q5_K",
    body: `256 weights in 176 bytes — <strong>5.50 bits each</strong>. Five bits per
      weight — 32 levels — with the same 6-bit scale-and-minimum layout
      <code>Q4_K</code> uses across eight groups of 32.`,
  },

  Q6_K: {
    term: "Q6_K",
    body: `256 weights in 210 bytes — <strong>6.56 bits each</strong>. Six bits per
      weight, 64 levels, with 8-bit scales for 16 sub-blocks. Often used for the tensors
      a recipe declines to quantize harder, such as output projections and embeddings.`,
  },

  Q8_K: {
    term: "Q8_K",
    body: `256 weights in 292 bytes — <strong>9.13 bits each</strong>. An intermediate
      type: it exists so that quantized dot products have a common 8-bit form to work
      in, and it carries per-group sums for that purpose. Files rarely store it.`,
  },

  /* -- i-quants (codebook lookups, importance-matrix fitted) --------------- */

  IQ1_S: {
    term: "IQ1_S",
    body: `256 weights in 50 bytes — <strong>1.56 bits each</strong>. Weights are not
      rounded one at a time. Each group of 8 is matched to the nearest of 2048 fixed
      ternary <strong>patterns</strong> and the file stores only the index, so what is
      preserved is the shape a group of weights makes rather than each weight's own value.
      Below about 2 bits there is no room for the latter, and which groups get the closest
      match is decided by an importance matrix measured on sample text.`,
  },

  IQ1_M: {
    term: "IQ1_M",
    body: `256 weights in 56 bytes — <strong>1.75 bits each</strong>. The same 8-weight
      ternary patterns <code>IQ1_S</code> matches against, with scales stored per group of
      16 rather than per superblock. There is no separate block scale field: the scale is
      gathered from four nibbles spread through the block.`,
  },

  IQ2_XXS: {
    term: "IQ2_XXS",
    body: `256 weights in 66 bytes — <strong>2.06 bits each</strong>. Eight weights at a
      time are matched to the nearest of 256 fixed <strong>patterns</strong> and stored as
      one index, with signs carried separately. Real weight groups fall into a limited set
      of shapes, so a few hundred patterns describe them for less than four levels per
      weight would cost — which is what makes about 2 bits workable at all.`,
  },

  IQ2_XS: {
    term: "IQ2_XS",
    /*
     * The one count in this family not taken from a grid in this repo — we do not
     * decode IQ2_XS, so there is nothing here to count. It is ggml's
     * `dequantize_row_iq2_xs` masking its index with `& 511`, which makes the table
     * 512 entries, and it brackets: XXS 256, XS 512, S 1024 (reviewer, 2026-08-19).
     */
    body: `256 weights in 74 bytes — <strong>2.31 bits each</strong>. The same 8-weight
      pattern matching as <code>IQ2_XXS</code>, over a table of 512 — twice
      <code>IQ2_XXS</code>'s — with a quarter-bit more per weight going to the scales.`,
  },

  IQ2_S: {
    term: "IQ2_S",
    body: `256 weights in 82 bytes — <strong>2.56 bits each</strong>. Eight-weight
      patterns again, over a table of 1024 — four times <code>IQ2_XXS</code>'s, so a
      closer match per group. The sign bits are packed into the upper half of the same
      field that holds the pattern indices rather than a field of their own.`,
  },

  IQ3_XXS: {
    term: "IQ3_XXS",
    body: `256 weights in 98 bytes — <strong>3.06 bits each</strong>. The same idea in
      groups of 4 rather than 8: each group is matched to one of 256 four-value
      <strong>patterns</strong>, with signs stored separately. Three bits is where pattern
      lookup and plain per-weight levels start to cost about the same.`,
  },

  IQ3_S: {
    term: "IQ3_S",
    body: `256 weights in 110 bytes — <strong>3.44 bits each</strong>. Groups of 4
      again, over a table of 512 — twice <code>IQ3_XXS</code>'s — read as two sub-blocks
      per step, with per-group scales. Same bits per weight as <code>Q3_K</code>, spent on
      a fitted table of patterns instead of on levels and scales.`,
  },

  IQ4_NL: {
    term: "IQ4_NL",
    body: `32 weights in 18 bytes — <strong>4.50 bits each</strong>. Sixteen levels per
      weight, but the levels are <strong>not evenly spaced</strong>: they come from a
      fixed table shaped to the distribution weights actually have, which is denser near
      zero than a uniform grid would be.`,
  },

  IQ4_XS: {
    term: "IQ4_XS",
    body: `256 weights in 136 bytes — <strong>4.25 bits each</strong>. The non-linear
      16-level table <code>IQ4_NL</code> uses, applied across a 256-weight superblock
      with 6-bit group scales, which is what brings the cost below
      <code>IQ4_NL</code>'s 4.50.`,
  },

  /* -- ternary ------------------------------------------------------------- */

  TQ1_0: {
    term: "TQ1_0",
    body: `256 weights in 54 bytes — <strong>1.69 bits each</strong>. Weights take one
      of three values. Three does not divide a byte, so five weights are packed into one
      byte as base-3 digits, which is what gets the cost below the 2 bits a
      two-bit-per-weight layout would need.`,
  },

  TQ2_0: {
    term: "TQ2_0",
    body: `256 weights in 66 bytes — <strong>2.06 bits each</strong>. The same three
      values as <code>TQ1_0</code>, stored two bits per weight instead of packed in
      base 3. It spends three-eighths of a bit more per weight and unpacks with a shift
      and a mask rather than division.`,
  },

  /* -- 4-bit float block formats ------------------------------------------- */

  MXFP4: {
    term: "MXFP4",
    body: `32 weights in 17 bytes — <strong>4.25 bits each</strong>. Each weight is a
      4-bit float with 1 sign, 2 exponent and 1 significant bit, and the block carries
      one shared 8-bit power-of-two scale. Having an exponent per weight is what
      separates it from a 4-bit integer format.`,
  },

  NVFP4: {
    term: "NVFP4",
    body: `64 weights in 36 bytes — <strong>4.50 bits each</strong>. The same 4-bit
      float element as <code>MXFP4</code> over a larger block, with the block scale
      stored as an 8-bit float rather than a power of two, so the scale itself can land
      between powers of two.`,
  },

  /* -- newer and rarer ----------------------------------------------------- */

  Q1_0: {
    term: "Q1_0",
    body: `128 weights in 18 bytes — <strong>1.13 bits each</strong>. Close to the
      floor for a format that still stores something per weight, with the block large
      enough that its scale costs almost nothing per weight.`,
  },

  Q2_0: {
    term: "Q2_0",
    body: `64 weights in 18 bytes — <strong>2.25 bits each</strong>. Four levels per
      weight with one scale per 64. Note that type id 42 has been used for more than
      one layout by different projects — TensorLens sizes this from the file's own
      layout rather than from the id when the two disagree.`,
  },
};

/**
 * The explanation for a storage type, or `undefined` if there is none.
 *
 * Returns `undefined` rather than a fallback for the reason in the header: a
 * type nobody here has read gets silence. Callers omit the sentence.
 */
export function explainForDtype(name: string): Explanation | undefined {
  /*
   * `Object.hasOwn`, not a bare index. A plain object inherits from
   * `Object.prototype`, so `DTYPE_EXPLANATIONS["constructor"]` answers with a
   * function and `["toString"]` with a method — and the caller's check is
   * `if (explanation)`, which both pass. A dtype string comes out of a file
   * header, so this is reachable by a crafted safetensors header rather than
   * only by a typo.
   */
  return Object.hasOwn(DTYPE_EXPLANATIONS, name) ? DTYPE_EXPLANATIONS[name] : undefined;
}
